import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["disabled"]
const _hoisted_2 = {
  key: 0,
  class: "flex flex-col gap-4 mt-5 w-full h-[22rem]"
}
const _hoisted_3 = { class: "relative h-full w-full" }
const _hoisted_4 = {
  key: 1,
  class: "flex flex-col py-5 mt-2 rounded-lg gap-8"
}
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "text-lg text-gray-900 font-semibold mr-2" }
const _hoisted_7 = { class: "bg-blue-50 border border-blue-200 rounded-full py-1 px-2 w-[90px] text-center text-blue-600 text-xs font-medium" }
const _hoisted_8 = { class: "flex gap-1 text-xs items-center" }
const _hoisted_9 = { class: "text-gray-600 font-semibold text-sm" }
const _hoisted_10 = {
  key: 0,
  class: "rounded-b-lg border-t border-t-gray-200 border-t-2"
}
const _hoisted_11 = {
  key: 0,
  class: "flex flex-col rounded-lg bg-white border border-gray-200"
}
const _hoisted_12 = { class: "text-lg text-gray-900 font-semibold mr-2" }
const _hoisted_13 = { class: "bg-blue-50 border border-blue-200 rounded-full py-1 px-2 w-[90px] text-center text-blue-600 text-xs" }
const _hoisted_14 = { class: "flex gap-1 text-xs items-center" }
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", { class: "text-gray-600 font-semibold text-sm" }, "View", -1)
const _hoisted_16 = {
  key: 0,
  class: "rounded-b-lg border-t border-t-gray-200 border-t-2"
}
const _hoisted_17 = { class: "relative" }
const _hoisted_18 = { class: "flex flex-col bg-white gap-3" }
const _hoisted_19 = { class: "flex flex-col items-center px-4 py-6" }
const _hoisted_20 = { class: "mt-6 font-semibold text-2xl text-gray-900" }
const _hoisted_21 = ["innerHTML"]
const _hoisted_22 = { class: "font-normal text-sm text-red-600 text-center mt-5" }
const _hoisted_23 = { class: "w-screen max-w-5xl" }
const _hoisted_24 = { class: "p-6 text-center" }
const _hoisted_25 = { class: "text-xl font-semibold text-gray-900 mb-8" }
const _hoisted_26 = { class: "text-gray-600 mb-8" }

import { computed, ref } from "vue";
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import StudentTable from "../components/StudentTable.vue";
import { useSchoolStore } from "@/stores/schoolStore";
import { useLearnerStore } from "@/stores/learnerStore";
import { useFilterStore } from "@/stores/filterStore";
import { useLicenseStore } from "@/stores/licenseStore";
import i18n from "@/plugins/i18n";
import { useUserStore } from "@/stores/userStore";
import BulkUploadLearners from "../components/BulkLearnersUploadComponent.vue";
import PreviewBulkUploadComponent from "../components/PreviewBulkUploadComponent.vue";
import FiltersOutOfDateComponent from "@/modules/main/components/FiltersOutOfDateComponent.vue";
import LoadingSpinnerComponent from "@/modules/main/components/LoadingSpinnerComponent.vue";
import PageHeaderComponent from "@/components/global/PageHeaderComponent.vue";

// Initialize the stores for learners, school, and subjects

export default /*#__PURE__*/_defineComponent({
  __name: 'StudentsView',
  setup(__props) {

const userStore = useUserStore();
const schoolStore = useSchoolStore();
const learnerStore = useLearnerStore();
const filterStore = useFilterStore();
const licenseStore = useLicenseStore();

const activeClassId = ref(null);
const showLearnersBulkUploadProgressModal = ref(false);
const searchLearner = ref("");
const showBulkUploadModal = ref(false);
const showPermissionDeniedModal = ref(false);
const showBulkUploadLearnersPreview = ref(false);

const learnersToUpload = ref<any>([]);

// Computed to get admin classGroupIds
const adminClassGroupIds = computed(() => userStore.getAdminClassGroupIds);

const filteredData = computed(() => {
  const filteredClassGroups = filterStore.selectedFilters.classGroup;

  if (!filteredClassGroups || filteredClassGroups.length === 0) {
    const gradeLevel = filterStore.selectedFilters.gradeLevel;
    const allClasses = schoolStore.getSchoolStructure?.classGroups.filter(
      (classItem) => classItem.gradeLevel === gradeLevel
    );
    const subjectFilter = filterStore.selectedFilters.subject;
    const finalClasses = allClasses
      ?.filter((item) => item.subject === subjectFilter)
      ?.sort((a, b) => {
        // Compare the `name` property alphanumerically
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
      });

    return finalClasses?.map((classGroupData) => {
      const subjectName =
        filterStore.filterOptions?.subjects.find(
          (subject) => subject._id === classGroupData.subject
        )?.name || "Unknown";

      const students = learnerStore.getSchoolLearners
        .filter(
          (student) =>
            student?.classes &&
            student.classes.some(
              (studentClass) => studentClass._id === classGroupData._id
            )
        )
        .map((student) => ({
          id: student._id,
          firstName: student.firstName,
          lastName: student.lastName,
          email: student.email,
          status: student.status,
          preferredLang: student.preferredLang,
        }));

      return {
        id: classGroupData._id,
        name: classGroupData.name,
        subject: subjectName,
        gradeLevel: classGroupData.gradeLevel,
        preferredLanguage: classGroupData.preferredLanguage,
        students,
      };
    });
  } else {
    // Get class group data for all selected class groups
    const classGroupsData = schoolStore.getSchoolStructure?.classGroups.filter(
      (group) => filteredClassGroups.includes(group._id)
    );

    const students = learnerStore.getSchoolLearners
      .filter(
        (student) =>
          student?.classes &&
          student.classes.some(
            (studentClass) => studentClass._id === classGroupsData._id
          )
      )
      .map((student) => ({
        id: student._id,
        firstName: student.firstName,
        lastName: student.lastName,
        email: student.email,
        status: student.status,
      }));
    // if (!classGroupsData || classGroupsData.length === 0) return [];

    // Map over each class group to build the required data
    const classGroupDetails = classGroupsData?.map((classGroupData) => {
      const subjectName =
        filterStore.filterOptions?.subjects.find(
          (subject) => subject._id === classGroupData.subject
        )?.name || "Unknown";

      const students = learnerStore.getSchoolLearners
        .filter(
          (student) =>
            student?.classes &&
            student.classes.some(
              (studentClass) => studentClass._id === classGroupData._id
            )
        )
        .map((student) => ({
          id: student._id,
          firstName: student.firstName,
          lastName: student.lastName,
          email: student.email,
          preferredLang: student.preferredLang,
        }));

      return {
        id: classGroupData._id,
        name: classGroupData.name,
        subject: subjectName,
        gradeLevel: classGroupData.gradeLevel,
        preferredLanguage: classGroupData.preferredLanguage,
        students,
      };
    });

    return classGroupDetails;
  }
});

/**
 * Get a list of students without a class group in the selected grade
 */
const noClassGroupStudents = computed(() => {
  //no classes OR has assessment but no class for filtered subject
  const students = learnerStore.getSchoolLearners.filter(
    (student) =>
      (
        !student.classes || student.classes.length === 0 ||
        (
          student.classes.every(
            (studentClass) => studentClass.subject.name !== filterStore.selectedSubject.name
          ) &&
          student.assessmentStates.some(
            (studentAssess) => studentAssess.subject === filterStore.selectedSubject.name.toUpperCase()
          )
        )
      ) &&
      student.grade === filterStore.selectedGrade
  );
  students.forEach((student) => {
    student.id = student._id;
  });
  return {
    id: filterStore.selectedSchool?.classes[0]?._id,
    name: i18n.global.t("students_view.no_assigned_classes"),
    subject: filterStore.selectedSubject.name,
    gradeLevel: filterStore.selectedGrade,
    preferredLanguage: "en",
    students,
  };
});

const loading = ref(false);

const isSchoolAdmin = computed(() => userStore.isSchoolAdmin);
const isInternalAdmin = computed(() => userStore.isInternalAdmin);
const isSchoolGroupAdmin = computed(() => userStore.isSchoolGroupAdmin);
const isClassGroupAdmin = computed(() => userStore.isClassGroupAdmin);

// Computed property to check if general actions are permitted
const canPerformGeneralAction = computed(
  () => isSchoolAdmin.value || isInternalAdmin.value || isSchoolGroupAdmin.value || isClassGroupAdmin.value
);

const handleGradeBulkUpload = () => {
  console.log("Can Perform General Action: ", canPerformGeneralAction.value);
  if (canPerformGeneralAction.value) {
    showBulkUploadModal.value = true;
  } else {
    showPermissionDeniedModal.value = true;
  }
};

const submitLearnersForUpload = async (learners: any) => {
  loading.value = true;
  try {
    // Submitting the uploaded learners for bulk creation in the learner store
    let request = await learnerStore.bulkCreateLearners(learners);
    showLearnersBulkUploadProgressModal.value = true;
    console.log("Request: ", request);
    loading.value = false;
  } catch (error) {
    console.error(error);
    loading.value = false;
  }
};

const uploadInProgress = () => {
  // Resetting the UI states after upload is in progress
  showLearnersBulkUploadProgressModal.value = false;
};

const toggleClass = (id: any) => {
  // Toggling the active class ID between the provided ID and null
  activeClassId.value = activeClassId.value === id ? null : id;
};

const receiveLearnersToUpload = (learners: any) => {
  console.log("Learners Received: ", learners);
  learnersToUpload.value = JSON.parse(JSON.stringify(learners));
  showBulkUploadLearnersPreview.value = true;
};

const ShowUploadOptions = () => {
  showBulkUploadLearnersPreview.value = false;
  showBulkUploadModal.value = true;
};

const handleFinalUpload = (learners: any) => {
  showBulkUploadLearnersPreview.value = false;
  showBulkUploadModal.value = false;
  console.log("Final Learners Received: ", learners);
  submitLearnersForUpload(learners);
};

const closeBulkUploadLearnersPreview = () => {
  showBulkUploadModal.value = false;
  showBulkUploadLearnersPreview.value = false;
  const schoolId = schoolStore.getCurrentSchool?._id;
  if (schoolId) {
    licenseStore.fetchLicenses(schoolId);
  }
};

const handleSearchQueryUpdate = (searchQuery: string) => {
  searchLearner.value = searchQuery;
  console.log("Search Query: ", searchQuery);
};

return (_ctx: any,_cache: any) => {
  const _component_rl_icon = _resolveComponent("rl-icon")!
  const _component_modal = _resolveComponent("modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", null, [
      _createVNode(PageHeaderComponent, {
        pageHeading: _ctx.$t('students_view.students'),
        "onUpdate:searchQuery": handleSearchQueryUpdate
      }, null, 8, ["pageHeading"]),
      _createElementVNode("button", {
        class: _normalizeClass('text-[#344054] text-semibold mt-5 py-2.5 px-4 rounded-lg flex gap-1 border border-gray-300 ' + (_unref(licenseStore).license == null || _unref(licenseStore).loader.fetchingLicenses? 'cursor-not-allowed' : 'cursor-pointer')),
        onClick: handleGradeBulkUpload,
        disabled: _unref(licenseStore).license==null || _unref(licenseStore).loader.fetchingLicenses
      }, [
        _createVNode(_component_rl_icon, {
          icon: 'rl-bulk-upload',
          title: 'rl bulk upload',
          "aria-hidden": "true"
        }),
        _createElementVNode("span", null, _toDisplayString(_ctx.$t("students_view.btn_upload")), 1)
      ], 10, _hoisted_1),
      (_unref(filterStore).filtersChanged === true)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(LoadingSpinnerComponent),
              _createVNode(FiltersOutOfDateComponent)
            ])
          ]))
        : _createCommentVNode("", true),
      (_unref(filterStore).filtersChanged === false)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredData.value, (gradeClass) => {
              return (_openBlock(), _createElementBlock("div", {
                key: gradeClass.id,
                class: "flex flex-col rounded-lg bg-white border border-gray-200"
              }, [
                _createElementVNode("div", {
                  onClick: ($event: any) => (toggleClass(gradeClass.id)),
                  class: "flex justify-between items-center bg-transparent text-white px-4 py-8 cursor-pointer"
                }, [
                  _createElementVNode("div", null, [
                    _createElementVNode("span", _hoisted_6, _toDisplayString(gradeClass.name), 1),
                    _createElementVNode("span", _hoisted_7, _toDisplayString(gradeClass.students.length) + " " + _toDisplayString(_ctx.$t("students_view.students")), 1)
                  ]),
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("p", _hoisted_9, _toDisplayString(_ctx.$t("students_view.view")), 1),
                    _createElementVNode("span", null, [
                      _createVNode(_component_rl_icon, {
                        icon: 'rl-down-center-arrow',
                        title: 'rl down center arrow',
                        "aria-hidden": "true"
                      })
                    ])
                  ])
                ], 8, _hoisted_5),
                (activeClassId.value === gradeClass.id)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                      _createVNode(StudentTable, {
                        students: gradeClass.students,
                        classId: gradeClass.id,
                        className: gradeClass.name,
                        gradeLevel: gradeClass.gradeLevel,
                        preferredLanguage: gradeClass.preferredLanguage,
                        subject: gradeClass.subject.toLowerCase(),
                        search: searchLearner.value,
                        onTransmitFinalLearners: handleFinalUpload,
                        allowedClassGroupIds: adminClassGroupIds.value
                      }, null, 8, ["students", "classId", "className", "gradeLevel", "preferredLanguage", "subject", "search", "allowedClassGroupIds"])
                    ]))
                  : _createCommentVNode("", true)
              ]))
            }), 128)),
            (noClassGroupStudents.value.students.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  _createElementVNode("div", {
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (toggleClass('noClassGroups'))),
                    class: "flex justify-between items-center bg-transparent text-white px-4 py-8 cursor-pointer"
                  }, [
                    _createElementVNode("div", null, [
                      _createElementVNode("span", _hoisted_12, _toDisplayString(_ctx.$t("students_view.no_assigned_classes")), 1),
                      _createElementVNode("span", _hoisted_13, _toDisplayString(noClassGroupStudents.value.students.length) + " " + _toDisplayString(_ctx.$t("students_view.students")), 1)
                    ]),
                    _createElementVNode("div", _hoisted_14, [
                      _hoisted_15,
                      _createElementVNode("span", null, [
                        _createVNode(_component_rl_icon, {
                          icon: 'rl-down-center-arrow',
                          title: 'rl down center arrow',
                          "aria-hidden": "true"
                        })
                      ])
                    ])
                  ]),
                  (activeClassId.value === 'noClassGroups')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                        _createVNode(StudentTable, {
                          students: noClassGroupStudents.value.students,
                          classId: noClassGroupStudents.value.id,
                          className: noClassGroupStudents.value.name,
                          gradeLevel: noClassGroupStudents.value.gradeLevel,
                          preferredLanguage: noClassGroupStudents.value.preferredLanguage,
                          subject: noClassGroupStudents.value.subject.toLowerCase(),
                          search: searchLearner.value,
                          onUploadedLearners: _ctx.fileUploadedLearners,
                          onCloseUploadModal: _ctx.toggleUi,
                          activitydata: _ctx.activitydata,
                          hidePrimaryNav: true
                        }, null, 8, ["students", "classId", "className", "gradeLevel", "preferredLanguage", "subject", "search", "onUploadedLearners", "onCloseUploadModal", "activitydata"])
                      ]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_modal, {
      showCloseButton: true,
      widthClass: 'max-w-lg',
      open: showLearnersBulkUploadProgressModal.value,
      onLocalModalOpen: _cache[1] || (_cache[1] = ($event: any) => (showLearnersBulkUploadProgressModal.value = $event))
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("div", _hoisted_19, [
              _createElementVNode("div", {
                class: "absolute top-6 right-4 cursor-pointer",
                onClick: uploadInProgress
              }, [
                _createVNode(_component_rl_icon, {
                  icon: 'rl-large-close',
                  title: 'rl large close',
                  "aria-hidden": "true"
                })
              ]),
              _createElementVNode("div", null, [
                _createVNode(_component_rl_icon, {
                  icon: 'rl-green-tick',
                  title: 'rl green tick',
                  "aria-hidden": "true"
                })
              ]),
              _createElementVNode("h1", _hoisted_20, _toDisplayString(_ctx.$t("bulk_learner_upload_component.modal.header")), 1),
              _createElementVNode("p", {
                class: "font-normal text-sm text-gray-600 text-center mt-2",
                innerHTML: _ctx.$t('bulk_learner_upload_component.modal.sub_header')
            
              }, null, 8, _hoisted_21),
              _createElementVNode("p", _hoisted_22, _toDisplayString(_ctx.$t("bulk_learner_upload_component.modal.important_info")), 1),
              _createElementVNode("button", {
                onClick: uploadInProgress,
                class: "font-semibold text-gray-700 text-base border-gray-300 border rounded-lg py-2 px-8 mt-8 w-full"
              }, _toDisplayString(_ctx.$t("students_view.done")), 1)
            ])
          ])
        ])
      ]),
      _: 1
    }, 8, ["open"]),
    (showBulkUploadModal.value)
      ? (_openBlock(), _createBlock(BulkUploadLearners, {
          key: 0,
          uploadType: 'grade',
          classId: 'classId',
          className: 'className',
          grade: _unref(filterStore).selectedFilters.gradeLevel,
          onLocalModalOpen: _cache[2] || (_cache[2] = ($event: any) => (showBulkUploadModal.value = false)),
          onResetModalState: _cache[3] || (_cache[3] = ($event: any) => (showBulkUploadModal.value = false)),
          onTransmitLearnersToUpload: receiveLearnersToUpload
        }, null, 8, ["grade"]))
      : _createCommentVNode("", true),
    _createVNode(_component_modal, {
      showCloseButton: false,
      widthClass: 'max-w-5xl',
      open: showBulkUploadLearnersPreview.value,
      onLocalModalOpen: _cache[6] || (_cache[6] = ($event: any) => (showBulkUploadLearnersPreview.value = $event))
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_23, [
          _createVNode(PreviewBulkUploadComponent, {
            uploadType: 'grade',
            grade: _unref(filterStore).selectedFilters.gradeLevel,
            subject: _unref(filterStore).selectedFilters.subject,
            learnersToUpload: learnersToUpload.value,
            onTransmitFinalLearners: handleFinalUpload,
            onCancelPreview: _cache[4] || (_cache[4] = ($event: any) => {
          showBulkUploadModal.value = false;
          showBulkUploadLearnersPreview.value = false;
        }),
            onResetState: _cache[5] || (_cache[5] = ($event: any) => (showBulkUploadModal.value = false)),
            onPreviousStep: ShowUploadOptions
          }, null, 8, ["grade", "subject", "learnersToUpload"])
        ])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(_component_modal, {
      showCloseButton: true,
      widthClass: 'max-w-sm',
      open: showPermissionDeniedModal.value,
      onLocalModalOpen: _cache[8] || (_cache[8] = ($event: any) => (showPermissionDeniedModal.value = $event))
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_24, [
          _createElementVNode("h2", _hoisted_25, _toDisplayString(_ctx.$t("modals.teacher_class_permission.title")), 1),
          _createElementVNode("p", _hoisted_26, _toDisplayString(_ctx.$t("modals.teacher_class_permission.description")), 1),
          _createElementVNode("button", {
            type: "button",
            onClick: _cache[7] || (_cache[7] = ($event: any) => (showPermissionDeniedModal.value = false)),
            class: "w-full rounded-md bg-white px-2.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          }, _toDisplayString(_ctx.$t("modals.teacher_class_permission.btns.ok")), 1)
        ])
      ]),
      _: 1
    }, 8, ["open"])
  ], 64))
}
}

})